import type { AfterViewInit, OnInit } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    HostListener,
    inject,
    TransferState,
    ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';

import { ModalHostDirective } from 'components/organisms/modal';
import { ThemeDirective } from 'src/directives/theme/theme.directive';
import { AnchorsService } from 'src/services/anchors/anchors.service';
import { BrowserConnectionService } from 'src/services/browser-connection/browser-connection.service';
import { ConsentManagementInitializerService } from 'src/services/consent-management-initializer/consent-management-initializer.service';
import { DeployUrlService } from 'src/services/deploy-url/deploy-url.service';
import { FontService } from 'src/services/font/font.service';
import { GtmInitializerService } from 'src/services/gtm-initializer/gtm-initializer.service';
import { ReferralCookiesService } from 'src/services/referral-cookies/referral-cookies.service';
import { SpeculationRulesService } from 'src/services/speculation-rules/speculation-rules.service';

import { APP_VERSION, APP_VERSION_KEY } from './app-version';

export const APP_CONTAINER = 'app-container';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterOutlet, ModalHostDirective, ThemeDirective],
})
export class AppComponent implements OnInit, AfterViewInit {
    private readonly appVersion = inject(APP_VERSION);
    private readonly transferState = inject(TransferState);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly anchorsService = inject(AnchorsService);
    private readonly deployUrlService = inject(DeployUrlService);
    private readonly referralCookiesService = inject(ReferralCookiesService);
    private readonly fontService = inject(FontService);
    private readonly browserConnectionService = inject(BrowserConnectionService);
    private readonly speculationRulesService = inject(SpeculationRulesService);
    private readonly gtmInitializerService = inject(GtmInitializerService);
    private readonly consentManagementInitializerService = inject(ConsentManagementInitializerService);

    public readonly APP_CONTAINER = APP_CONTAINER;

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {
        this.anchorsService.handleAnchorClick(event);
    }

    public ngOnInit(): void {
        this.initializeServices();
        this.setupTransferState();
    }

    public ngAfterViewInit(): void {
        import('focus-visible/dist/focus-visible.min.js');
    }

    private initializeServices(): void {
        this.fontService.init();
        this.browserConnectionService.init();
        this.gtmInitializerService.init();
        this.consentManagementInitializerService.init();
        this.deployUrlService.init();
        this.speculationRulesService.init();
        this.referralCookiesService.setCookies();
        this.anchorsService.initialize(this.router, this.activatedRoute);
    }

    private setupTransferState(): void {
        if (!this.transferState.hasKey(APP_VERSION_KEY)) {
            this.transferState.set(APP_VERSION_KEY, this.appVersion);
        }
    }
}
